import React, { useEffect, useState } from 'react';
import { SDG_TITLES } from '../../Form/types/collaboration';
import {
  getStaticQuestionDetail,
  createStaticQuestion,
  updateStaticQuestion,
  StaticQuestionCreatePayload,
} from '../../Game/api/gameApi';

interface OptionDraft {
  text: string;
  isCorrect: boolean;
}

const MIN_OPTIONS = 2;
const MAX_OPTIONS = 6;

const createEmptyOption = (): OptionDraft => ({ text: '', isCorrect: false });

const defaultOptions = (): OptionDraft[] => [
  createEmptyOption(),
  createEmptyOption(),
  createEmptyOption(),
  createEmptyOption(),
];

interface QuestionEditModalProps {
  questionId: number | null; // null = create mode
  onClose: () => void;
  onSaved: () => void;
}

const QuestionEditModal: React.FC<QuestionEditModalProps> = ({ questionId, onClose, onSaved }) => {
  const isCreateMode = questionId === null;

  const [questionText, setQuestionText] = useState('');
  const [options, setOptions] = useState<OptionDraft[]>(defaultOptions());
  const [sdgNumbers, setSdgNumbers] = useState<number[]>([]);

  const [loading, setLoading] = useState(!isCreateMode);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    if (isCreateMode) return;

    (async () => {
      try {
        setLoading(true);
        const detail = await getStaticQuestionDetail(questionId);
        setQuestionText(detail.question_text);
        setOptions(
          detail.options.map((o) => ({ text: o.text, isCorrect: o.is_correct })),
        );
        setSdgNumbers(detail.sdg_numbers ?? []);
      } catch (err) {
        setLoadError(err instanceof Error ? err.message : 'Failed to load question');
      } finally {
        setLoading(false);
      }
    })();
  }, [isCreateMode, questionId]);

  const markDirty = () => setIsDirty(true);

  const handleClose = () => {
    if (isDirty && !window.confirm('You have unsaved changes. Are you sure you want to close?')) return;
    onClose();
  };

  const handleOptionTextChange = (index: number, value: string) => {
    setOptions((prev) => prev.map((o, i) => (i === index ? { ...o, text: value } : o)));
    markDirty();
  };

  const handleMarkCorrect = (index: number) => {
    setOptions((prev) => prev.map((o, i) => ({ ...o, isCorrect: i === index })));
    markDirty();
  };

  const handleAddOption = () => {
    if (options.length >= MAX_OPTIONS) return;
    setOptions((prev) => [...prev, createEmptyOption()]);
    markDirty();
  };

  const handleRemoveOption = (index: number) => {
    if (options.length <= MIN_OPTIONS) return;
    setOptions((prev) => prev.filter((_, i) => i !== index));
    markDirty();
  };

  const toggleSdg = (num: number) => {
    setSdgNumbers((prev) =>
      prev.includes(num) ? prev.filter((n) => n !== num) : [...prev, num],
    );
    markDirty();
  };

  const validate = (): string | null => {
    if (!questionText.trim()) return 'Question text is required.';
    if (options.some((o) => !o.text.trim())) return 'All options must have text.';
    if (!options.some((o) => o.isCorrect)) return 'Select which option is correct.';
    if (sdgNumbers.length === 0) return 'Select at least one SDG.';
    return null;
  };

  const handleSave = async () => {
    const validationError = validate();
    if (validationError) {
      setSaveError(validationError);
      return;
    }

    const payload: StaticQuestionCreatePayload = {
      question_text: questionText.trim(),
      options: options.map((o) => ({ text: o.text.trim(), is_correct: o.isCorrect })),
      sdg_numbers: sdgNumbers,
    };

    try {
      setSaving(true);
      setSaveError(null);
      if (isCreateMode) {
        await createStaticQuestion(payload);
      } else {
        await updateStaticQuestion(questionId, payload);
      }
      onSaved();
      onClose();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save question');
    } finally {
      setSaving(false);
    }
  };

  const baseInputCls = `w-full px-3 py-2 border border-gray-300 rounded-lg text-sm
    focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4 flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b flex-shrink-0">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              {isCreateMode ? 'Add Question' : 'Edit Question'}
            </h2>
            {!isCreateMode && <p className="text-sm text-gray-500">ID: {questionId}</p>}
          </div>
          <div className="flex items-center gap-3">
            {isDirty && (
              <span className="text-xs text-amber-600 bg-amber-50 border border-amber-200 px-2 py-1 rounded-full">
                Unsaved changes
              </span>
            )}
            <button
              onClick={handleClose}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition-colors"
              title="Close"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
              <span className="ml-3 text-gray-500">Loading question...</span>
            </div>
          ) : loadError ? (
            <div className="text-center py-16">
              <p className="text-red-600 font-medium">{loadError}</p>
              <button onClick={onClose} className="mt-4 text-sm text-blue-600 hover:underline">Close</button>
            </div>
          ) : (
            <div className="space-y-5">
              {/* Question text */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Question Text <span className="text-red-500 ml-1">*</span>
                </label>
                <textarea
                  value={questionText}
                  onChange={(e) => {
                    setQuestionText(e.target.value);
                    markDirty();
                  }}
                  rows={3}
                  className={`${baseInputCls} resize-y`}
                  placeholder="e.g. Which SDG does this scenario support?"
                />
              </div>

              {/* Options */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium text-gray-700">
                    Answer Options <span className="text-red-500 ml-1">*</span>
                  </label>
                  <button
                    type="button"
                    onClick={handleAddOption}
                    disabled={options.length >= MAX_OPTIONS}
                    className="text-sm text-blue-600 hover:text-blue-800 hover:underline disabled:text-gray-400 disabled:no-underline disabled:cursor-not-allowed"
                  >
                    + Add option
                  </button>
                </div>
                <p className="text-xs text-gray-400 mb-2">Select the radio button next to the correct answer.</p>

                <div className="space-y-2">
                  {options.map((option, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <input
                        type="text"
                        value={option.text}
                        onChange={(e) => handleOptionTextChange(index, e.target.value)}
                        placeholder={`Option ${index + 1}`}
                        className={baseInputCls}
                      />
                      <label className="flex items-center gap-1.5 text-sm text-gray-600 whitespace-nowrap px-2">
                        <input
                          type="radio"
                          name="correct-option"
                          checked={option.isCorrect}
                          onChange={() => handleMarkCorrect(index)}
                          className="h-4 w-4 text-green-600"
                        />
                        Correct
                      </label>
                      <button
                        type="button"
                        onClick={() => handleRemoveOption(index)}
                        disabled={options.length <= MIN_OPTIONS}
                        className="p-2 text-gray-400 hover:text-red-600 disabled:text-gray-200 disabled:cursor-not-allowed transition-colors"
                        title="Remove option"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* SDG multiselect */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  SDGs <span className="text-red-500 ml-1">*</span>
                </label>
                <p className="text-xs text-gray-400 mb-1">
                  The first SDG selected determines which Guardian this question belongs to.
                </p>
                <div className="border border-gray-300 rounded-lg p-3 max-h-52 overflow-y-auto grid grid-cols-2 gap-1">
                  {SDG_TITLES.map((title: string, i: number) => {
                    const num = i + 1;
                    const checked = sdgNumbers.includes(num);
                    return (
                      <label
                        key={num}
                        className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer text-sm transition-colors select-none
                          ${checked ? 'bg-blue-50 text-blue-800' : 'hover:bg-gray-50 text-gray-700'}`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleSdg(num)}
                          className="h-4 w-4 text-blue-600 rounded flex-shrink-0"
                        />
                        <span>SDG {num}: {title}</span>
                      </label>
                    );
                  })}
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  {sdgNumbers.length > 0
                    ? `Selected: ${sdgNumbers}`
                    : 'None selected'}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {!loading && !loadError && (
          <div className="px-6 py-4 border-t bg-gray-50 rounded-b-xl flex-shrink-0">
            {saveError && <p className="text-sm text-red-600 mb-3">{saveError}</p>}
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={handleClose}
                disabled={saving}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300
                           rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-5 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg
                           hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default QuestionEditModal;